import { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import type { InvitedModes } from "api/models/draftRequest.model";
import { useGetDraftProviderOptions } from "api/queries/draftRequest/useGetDraftProviderOptions";
import useDebounce from "hooks/useDebounce";
import { useRequestFormSaveOrNull } from "@RequestFormV2/context/save/RequestFormSaveContext";
import { useRequestFormDraftId } from "@RequestFormV2/hooks/useRequestFormDraftId";
import { mapProviderOptions } from "@RequestFormV2/sections/Provider/utils/mapProviderOptions";
import { pruneUnavailableProviders } from "@RequestFormV2/sections/Provider/utils/pruneUnavailableProviders";
import type { SelectedParcelAccount } from "@RequestFormV2/sections/Provider/types";
import { getInvitedModeKeysForApi } from "@RequestFormV2/utils/invitedModesHelpers";

type UseDraftProviderOptionsParams = {
  pruneStaleSelections?: boolean;
  enabled?: boolean;
};

export function useDraftProviderOptions({
  pruneStaleSelections = false,
  enabled = true,
}: UseDraftProviderOptionsParams = {}) {
  const draftId = useRequestFormDraftId();
  const { getValues, setValue } = useFormContext();
  const saveContext = useRequestFormSaveOrNull();

  const invitedModes = useWatch({ name: "invited_modes" }) as InvitedModes | undefined;
  const freightModesString = getInvitedModeKeysForApi(invitedModes).join(",");
  const debouncedFreightModes = useDebounce(freightModesString, 1500);

  const {
    data: providerOptionsData,
    isLoading,
    isFetching,
  } = useGetDraftProviderOptions({
    draftId,
    freightModes: debouncedFreightModes,
    form: "v2",
    options: {
      enabled: enabled && !!draftId && !!debouncedFreightModes,
    },
  });

  const providerOptions = useMemo(
    () => mapProviderOptions(providerOptionsData?.invited_providers),
    [providerOptionsData?.invited_providers],
  );

  useEffect(() => {
    if (!pruneStaleSelections || !providerOptionsData?.invited_providers) {
      return;
    }

    const selectedProviders: string[] = getValues("invited_providers.providers") ?? [];
    const selectedParcelAccounts: SelectedParcelAccount[] = getValues("invited_providers.parcel_accounts") ?? [];
    const { providers, parcel_accounts, changed } = pruneUnavailableProviders(
      selectedProviders,
      selectedParcelAccounts,
      providerOptions,
    );

    if (!changed) {
      return;
    }

    setValue("invited_providers.providers", providers);
    setValue("invited_providers.parcel_accounts", parcel_accounts);

    saveContext?.saveField("invited_providers", { providers, parcel_accounts }).catch(() => undefined);
  }, [getValues, pruneStaleSelections, providerOptions, providerOptionsData?.invited_providers, saveContext, setValue]);

  return useMemo(
    () => ({
      providerOptions,
      minimumInvitesPossible: providerOptionsData?.minimum_invites_possible ?? 0,
      isLoading,
      isFetching,
    }),
    [providerOptions, providerOptionsData?.minimum_invites_possible, isLoading, isFetching],
  );
}
